import { useEffect, useState } from "react";
import { api } from "../lib/api";
import MentionTextarea from "./MentionTextarea";
import UserAvatar from "./UserAvatar";
import RichText from "./RichText";

export default function CommentsThread({ postId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/posts/${postId}/comments`);
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      await api.post(`/posts/${postId}/comments`, {
        content: content.trim(),
        parent_comment_id: replyTo,
      });
      setContent("");
      setReplyTo(null);
      await load();
    } finally {
      setPosting(false);
    }
  };

  const remove = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    await api.delete(`/posts/${postId}/comments/${commentId}`);
    await load();
  };

  const renderComment = (c, depth = 0) => (
    <div
      key={c.id}
      className={`flex gap-3 ${depth ? "ml-10 pl-3 border-l border-[rgba(0,170,255,0.18)]" : ""}`}
      data-testid={`comment-${c.id}`}
    >
      <UserAvatar
        user={{
          id: c.author?.id,
          handle: c.author?.handle,
          name: c.author?.name,
          avatar_url: c.author?.avatar_url,
          type: c.author?.type,
        }}
        size="sm"
        data-testid={`comment-avatar-${c.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm min-w-0">
            {c.author?.handle ? (
              <a
                href={`/u/${c.author.handle}`}
                className="text-white hover:text-[#00aaff] truncate"
                data-testid={`comment-author-name-${c.id}`}
              >
                {c.author?.name}
              </a>
            ) : (
              <span className="text-white truncate">{c.author?.name}</span>
            )}
            <span className="font-mono-tech text-[10px] text-[#94a3b8] truncate">
              @{c.author?.handle} · {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
            </span>
          </div>
          {c.can_delete && (
            <button onClick={() => remove(c.id)} className="text-[10px] text-[#94a3b8] hover:text-red-400 shrink-0">Delete</button>
          )}
        </div>
        <div className="text-sm text-white whitespace-pre-wrap mt-0.5 csc-break-anywhere">
          <RichText text={c.content} />
        </div>
        {depth === 0 && (
          <button
            onClick={() => setReplyTo(c.id)}
            className="mt-1 text-[11px] font-mono-tech uppercase tracking-wider text-[#94a3b8] hover:text-[#00aaff]"
            data-testid={`reply-${c.id}`}
          >
            Reply
          </button>
        )}
      </div>
    </div>
  );

  // group: top-level first, then replies under each parent (single nesting level)
  const top = items.filter((c) => !c.parent_comment_id);
  const repliesByParent = items.reduce((acc, c) => {
    if (c.parent_comment_id) {
      (acc[c.parent_comment_id] = acc[c.parent_comment_id] || []).push(c);
    }
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid={`comments-thread-${postId}`}>
      <form onSubmit={submit} className="space-y-2">
        {replyTo && (
          <div className="flex items-center justify-between text-[11px] font-mono-tech text-[#94a3b8]">
            <span>Replying to comment</span>
            <button type="button" onClick={() => setReplyTo(null)} className="hover:text-[#00aaff]">Cancel</button>
          </div>
        )}
        <MentionTextarea
          value={content}
          onChange={setContent}
          placeholder="Add a comment… use @ to mention, # for hashtags"
          rows={2}
          data-testid="comment-textarea"
        />
        <div className="flex justify-end">
          <button disabled={posting || !content.trim()} className="csc-btn-primary !py-2 !px-4" data-testid="comment-submit">
            {posting ? "Posting…" : "Comment"}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="font-mono-tech text-xs text-[#94a3b8] uppercase tracking-wider">Loading comments…</div>
      ) : top.length === 0 ? (
        <div className="font-mono-tech text-xs text-[#94a3b8] uppercase tracking-wider">No comments yet.</div>
      ) : (
        <div className="space-y-3">
          {top.map((c) => (
            <div key={c.id} className="space-y-2">
              {renderComment(c)}
              {(repliesByParent[c.id] || []).map((r) => renderComment(r, 1))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
